/* eslint-disable no-nested-ternary */
import noop from "lodash/noop";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { SpinnerFullScreen } from "../../../components/SpinnerFullScreen";
import { WarningModal } from "../../../components/WarningModal";
import { AtiraSpacePlanType } from "../../../model/plan/types/AtiraSpacePlanType.enum";
import { paymentSliceSelectors } from "../../../redux/payment/payment.selector";
import { paymentActions } from "../../../redux/payment/payment.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { userActions } from "../../../redux/user/user.slice";
import { AtiraToast } from "../../../utils/AtiraToast";
import { standardDate } from "../../../utils/Date";
import { getPlanTitle } from "../../../utils/pricingUtils";
import { PrivatePlansTable } from "./PrivatePlansTable";

enum NoticeModalType {
  CANCEL = "CANCEL",
  RESUME = "RESUME",
}

export const PrivatePricingPlans: React.FC = () => {
  const [planId, setPlanId] = useState<string | undefined>(undefined);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);
  const [mode, setMode] = useState<NoticeModalType | null>(null);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const plans = useAppSelector(paymentSliceSelectors.selectPlans);
  const loading = useAppSelector(paymentSliceSelectors.selectPlansLoading);

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const createCheckout = useCallback(
    async (planId: string, seats: number) => {
      try {
        setPlanId(planId);
        const checkoutURL = await dispatch(
          paymentActions.createCheckout({
            productId: planId,
            userId,
            embed: true,
            seats,
          }),
        ).unwrap();
        window.createLemonSqueezy();
        window.LemonSqueezy.Url.Open(checkoutURL);
        paymentActions.resetCheckoutURL();
        setPlanId(undefined);
      } catch (e: any) {
        AtiraToast.apiError(e);
        console.log(e);
      }
    },
    [dispatch, userId],
  );

  const onCancelPlan = async () => {
    try {
      setButtonsDisabled(true);
      setPlanId(user?.plan?.lemonVariantId!);
      await dispatch(paymentActions.cancelPlan()).unwrap();
      await dispatch(userActions.getLoggedInUser({ userId })).unwrap();
      setMode(null);
      AtiraToast.success(t("subscription.plan.noticeModal.cancel.toast"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setButtonsDisabled(false);
      setPlanId(undefined);
    }
  };

  const onResumePlan = async () => {
    try {
      setButtonsDisabled(true);
      setPlanId(user?.plan?.lemonVariantId!);
      await dispatch(paymentActions.resumeCancelledPlan()).unwrap();
      await dispatch(userActions.getLoggedInUser({ userId })).unwrap();
      setMode(null);
      AtiraToast.success(t("subscription.plan.noticeModal.resume.toast"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setButtonsDisabled(false);
      setPlanId(undefined);
    }
  };

  const noticeModalData: Record<
    NoticeModalType,
    {
      title: string;
      description: string;
      confirmButtonText: string;
      closeButtonText: string;
      onClick: VoidFunction;
    }
  > = {
    [NoticeModalType.CANCEL]: {
      title: t("subscription.plan.noticeModal.cancel.title", {
        plan: user?.plan?.type ? t(getPlanTitle(user?.plan?.type)) : "",
      }),
      description: t("subscription.plan.noticeModal.cancel.description", {
        date: standardDate(user?.plan?.lemonPlanData?.renewsAt!),
      }),
      confirmButtonText: t("subscription.plan.noticeModal.cancel.button.ok"),
      closeButtonText: t("subscription.plan.noticeModal.cancel.button.no"),
      onClick: onCancelPlan,
    },
    [NoticeModalType.RESUME]: {
      title: t("subscription.plan.noticeModal.resume.title", {
        plan: user?.plan?.type ? t(getPlanTitle(user?.plan?.type)) : "",
      }),
      description: t("subscription.plan.noticeModal.resume.description", {
        date: standardDate(user?.plan?.lemonPlanData?.renewsAt!),
      }),
      confirmButtonText: t("common.resume"),
      closeButtonText: t("common.no"),
      onClick: onResumePlan,
    },
  };

  useEffect(() => {
    dispatch(paymentActions.getAllPlans());
  }, [dispatch]);

  useEffect(() => {
    if (localStorage.getItem("processCheckout") === "true" && user) {
      const plan = plans?.find(
        ({ _id }) => _id === localStorage.getItem("plan"),
      );
      const seatsCount = localStorage.getItem("seats");

      localStorage.setItem("processCheckout", "false");

      if (plan && plan.type !== AtiraSpacePlanType.PLAN_0) {
        // TODO Update the outside plans to support seats and pass it here
        createCheckout(
          plan.lemonVariantId,
          seatsCount ? Number(seatsCount) : 5,
        );
      }

      // This is a temp flag sat by signupFlow to avoid multiple API calls;
      // We remove it here
      setTimeout(() => {
        localStorage.removeItem("processCheckout");
        localStorage.removeItem("plan");
        localStorage.removeItem("mode");
        localStorage.removeItem("seats");
      }, 3000);
    }
  }, [createCheckout, plans, user]);

  if (loading && !plans?.length) {
    return <SpinnerFullScreen containerHeight={"50vh"} />;
  }

  return (
    <Fragment>
      <PrivatePlansTable
        plans={plans ?? []}
        onCreateCheckout={createCheckout}
        setMode={setMode}
        buttonsDisabled={buttonsDisabled}
        planId={planId}
      />

      <WarningModal
        isOpen={Boolean(mode)}
        title={mode ? noticeModalData[mode]?.title : ""}
        description={mode ? noticeModalData[mode]?.description : ""}
        confirmButtonText={mode ? noticeModalData[mode]?.confirmButtonText : ""}
        denyButtonText={mode ? noticeModalData[mode]?.closeButtonText : ""}
        onClose={() => setMode(null)}
        onConfirm={mode ? noticeModalData[mode]?.onClick : noop}
        loading={Boolean(planId) && Boolean(mode) && buttonsDisabled}
      />
    </Fragment>
  );
};
